import { useMemo, useEffect, useState, forwardRef, useImperativeHandle, useCallback, useRef } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Alert } from "@material-ui/lab";
import { Button, Icon, Typography } from "@material-ui/core";

import { RATE_OPTIONS, DRIVER_NOTIFICATION_OPTIONS, TRIP_NOTIFICATION_OPTIONS } from "app/main/consts";
import {
	hideRate,
	onClickCreateUrl,
	getDisplayPickupAddress,
	getDisplayDeliveryAddress,
	getNextStatusExternal,
	STATUS_TRIPS,
} from "app/main/utils/tripsUtils";
import { openFormDialog, openLoadedFormDialog, refreshDialog, closeFormDialog } from "app/store/tools/formDialogSlice";
import {
	getRoleLabel,
	getUserTier,
	getAuthToken,
	isCarrierPermission,
	isInternalPermission,
	isDriver,
} from "app/services/LoginService";
import { useTripInformation } from "./hooks/useTripInformation";
import { handleFileUpload, handledLocationsUpdated } from "app/main/profile/trips/hooks/createTrip";
import { hasRequiredGateKeepers, processTierRestrictions, roleRestrictions } from "app/main/utils/rolesUtils";
import { showSnackbar } from "app/main/utils/snackbarUtil";
import { SmarthopConfirmDialog } from "@smarthop/form";
import { incrementDataRevision } from "app/store/tools/revisionSlice";
import {
	saveTripRC,
	sendItinerary,
	sendBolNotification,
	updateTrip,
	updateTripStatus,
	sendBrokerUpdate,
	generateOrUpdateRelayTrips,
	removeRelayTrips,
	getTrip,
	initiateCheckCall,
} from "app/services/tripsServices";
import { buildLink } from "app/services/fileServices";
import SmarthopDialogViewContainer from "@smarthop/views/SmarthopDialogViewContainer";
import SmarthopDialogViewLoadingStub from "@smarthop/views/SmarthopDialogViewLoadingStub";
import TrackingCellView from "@smarthop/list/views/TrackingCellView";
import TripsCellView from "@smarthop/list/views/TripsCellView";
import TruckCellView from "@smarthop/list/views/TruckCellView";
import TripCreateTranscription from "./TripCreateTranscription";
import CostsBreakdownPage from "app/main/profile/cost-structure/calculator/CostsBreakdownPage";
import TripDescription from "./TripDescription";
import { uploadFiles } from "app/main/multi-file-upload/MultiFileUploadView";
import uniqid from "uniqid";
import TripPayrollPage from "../users/TripPayrollPage";
import { isEnabled } from "app/services/featureStorageService";
import TripStatusSelector from "./TripStatusSelector";
import TripCheckCallConfirmModal from "./TripCheckCallConfirmModal";

import { tripCommentsList, loadHistoryList } from "@smarthop/list";
import TripCommentPanel from "app/main/profile/trips/TripCommentPanel";
import WarningConfirmDialog from "../../common/WarningConfirmDialog";
import PaywallDialog from "app/main/billing/paywall/PaywallDialog.js";

// Segment
import { CALENDAR_EVENT } from "app/main/segment/segmentType";
import { createTrackOrPage } from "app/main/segment/segmentEvent";
import { renderDataItem } from "app/main/utils/uiUtils";

const _ERROR_PARSE_DOC = `We failed to parse uploaded documents, it's still uploaded but please transcribe document manually.`;
const _SUCCESS_PARSE_DOC = `We successfully parsed uploaded documents, please verify all information before saving.`;
const _DEFAULT_TRANSCRIPTION = { flag: false, message: null, type: null, showEdit: false, optionsText: {} };
const _FAILED_TRANSCRIPTION = {
	flag: true,
	showEdit: false,
	optionsText: { accept: "Transcribe Manually", close: "Cancel" },
	error: true,
};
const _SUCCESS_TRANSCRIPTION = {
	flag: true,
	showEdit: true,
	message: _SUCCESS_PARSE_DOC,
	optionsText: { accept: "Save and Verify", edit: "Edit transcription", close: "Cancel" },
};

// Colors for relay trip routes - similar to TruckDetailsView
const RELAY_TIMELINE_COLORS = ["bg-green", "bg-blue", "bg-purple-600", "bg-brown", "bg-pink-800"];
const RELAY_COLORS_MAPPING = {
	"bg-green": "#4CAF50",
	"bg-blue": "#2196F3",
	"bg-purple-600": "#9C27B0",
	"bg-brown": "#795548",
	"bg-pink-800": "#AD1457",
};

const extractDriverPhone = (trip) => {
	if (!trip) return "";

	const candidatePaths = [
		["driver__view", "metadata", "phone"],
		["driver__view", "metadata", "driver", "phone"],
		["driver__view", "metadata", "driver", "metadata", "phone"],
		["driver_phone"],
		["driver", "phone"],
		["truck__view", "metadata", "driver__view", "metadata", "phone"],
		["profitData", "data", "driver", "phone"],
	];

	for (const path of candidatePaths) {
		let value = trip;
		for (const key of path) {
			if (value && typeof value === "object" && key in value) {
				value = value[key];
			} else {
				value = undefined;
				break;
			}
		}

		if (typeof value === "string" && value.trim().length > 0) {
			return value.trim();
		}
	}

	return "";
};

const getRelayTripStopDisplay = (trip, stopType) => {
	const allLocations = (trip?.all_locations ?? trip?.planLocations ?? []).map((location) => ({
		...location,
		type: location?.type ?? location?.stopType,
	}));

	const normalizedTrip = {
		data: {
			all_locations: allLocations,
			pickup_address: trip?.pickup_address ?? trip?.data?.pickup_address,
			delivery_address: trip?.delivery_address ?? trip?.data?.delivery_address,
		},
	};

	const value =
		stopType === "PICKUP" ? getDisplayPickupAddress(normalizedTrip) : getDisplayDeliveryAddress(normalizedTrip);
	return value || "-";
};

const getRelayTripData = async (parentTrip, carrierId) => {
	if (!parentTrip?.is_relay_parent || !parentTrip?.relay_trips?.length) {
		return { childTrips: [], routes: [], failedCount: 0 };
	}

	try {
		const relayTripPromises = parentTrip.relay_trips.map((tripId) =>
			getTrip(tripId, carrierId)
				.then((trip) => ({ trip }))
				.catch((err) => {
					console.warn(`Failed to fetch relay trip ${tripId}:`, err);
					return { trip: null };
				})
		);

		const relayTripResults = await Promise.all(relayTripPromises);
		const childTrips = relayTripResults.map((item) => item.trip).filter((trip) => trip !== null);
		const failedCount = relayTripResults.filter((item) => item.trip === null).length;

		const routes = childTrips
			.map((trip, index) => {
				const colorKey = RELAY_TIMELINE_COLORS[index % RELAY_TIMELINE_COLORS.length];
				const color = RELAY_COLORS_MAPPING[colorKey];
				const rawLocations = trip?.planLocations ?? trip?.all_locations ?? [];

				const locations = rawLocations
					.map((location) => {
						return {
							...location,
							lat: location?.coordinates?.lat ?? location?.lat,
							lng: location?.coordinates?.long ?? location?.lng ?? location?.coordinates?.lng,
							type: location?.type ?? location?.stopType,
						};
					})
					.filter((location) => location.lat && location.lng);

				return {
					type: "RELAY_TRIP",
					routeId: trip._id,
					locations,
					color,
					tripData: trip,
				};
			})
			.filter((route) => route.locations.length > 0);

		return { childTrips, routes, failedCount };
	} catch (error) {
		console.error("Error fetching relay trip data:", error);
		return { childTrips: [], routes: [], failedCount: parentTrip?.relay_trips?.length ?? 0 };
	}
};

const getRelayTripView = (trip) => {
	if (trip?.trip__view?.value) return trip.trip__view;

	return {
		value: trip?._id,
		label: trip?.load_id ? `# ${trip.load_id}` : trip?._id,
		metadata: {
			carrier: trip?.carrier,
			status: trip?.status ?? trip?.data?.status,
			broker: trip?.broker ?? trip?.data?.broker,
			smartpay_eligible: trip?.smartpay_eligible,
		},
	};
};

const getRelayTruckView = (trip) => {
	if (trip?.truck__view?.value) return trip.truck__view;

	return {
		value: trip?.truck,
		label: trip?.truck__view?.label ?? "-",
		metadata: {
			carrier: trip?.carrier,
			trailerid: trip?.truck_trailer__view?.metadata?.trailerid,
		},
	};
};

const getRelayTrackingView = (trip) => {
	const status = trip?.status ?? trip?.data?.status;
	if (
		[...STATUS_TRIPS.delivered, ...STATUS_TRIPS.cancelled].includes(status) ||
		STATUS_TRIPS.pending.includes(status)
	) {
		return null;
	}
	return trip?.tracking__view ?? null;
};

const getRelayTrackingItem = (trip) => {
	if (trip?.data) return trip;
	return {
		...trip,
		data: {
			status: trip?.status,
			delivery_date: trip?.delivery_date,
			delivery_window_date: trip?.delivery_window_date,
		},
	};
};

const TripView = forwardRef(({ nativeMobile, dataIds, onProgress, setTitle, onLoading }, ref) => {
	useEffect(() => {
		setTitle?.("Trip Details");
		if (dataIds?.fromCalendar) {
			createTrackOrPage(CALENDAR_EVENT, { type: "trip" }, "track");
		}
		// eslint-disable-next-line
	}, []);

	const dispatch = useDispatch();
	const snackbar = useSnackbar();

	const user = useSelector(({ auth }) => auth.user);

	const tripId = dataIds?.tripId;
	const isExternal = dataIds?.isExternal;
	const readOnly = dataIds.readOnly;
	const carrierId = dataIds?.carrierId;
	const reactNative = window?.ReactNativeWebView ?? false;

	const isLogged = !!getAuthToken();
	const role = useMemo(() => getRoleLabel(), []);
	const isAdmin = role === "administrator";
	const userTier = getUserTier();
	const hasProfessionalRestrictions = userTier
		? !processTierRestrictions({ restrictions: ["TIER_PROFESSIONAL"], tier: userTier })
		: true;
	const hasBasicRestrictions = userTier
		? !processTierRestrictions({ restrictions: ["TIER_BASIC"], tier: userTier })
		: true;

	const isManager = ["administrator", "carrier", "carrier_manager"].includes(role);
	const isInternal = useMemo(() => isInternalPermission(role), [role]);
	const isCarrier = useMemo(() => isCarrierPermission(role), [role]);
	const isCarrierDriver = useMemo(() => isDriver(), []);
	const isPublicView = useMemo(() => !!dataIds?.public || dataIds?.public === "true", [dataIds]);
	const [trip, tripPlan, tripInvoice, hidePrice, error] = useTripInformation(dataIds, onLoading, nativeMobile);

	// Payroll and Invoicing features check
	const isTripInvoicingEligible = trip?.enterprise_features?.invoicingModuleOnboarding === "COMPLETE";
	const invoiceHasAccessView = hasRequiredGateKeepers(user, { permission_invoice_access: "viewer" });
	const invoiceHasAccessEdit = hasRequiredGateKeepers(user, { permission_invoice_access: "editor" });
	const payrollHasAccessEdit = hasRequiredGateKeepers(user, { permission_payroll_access: "editor" });
	const isPayrollEnabled = trip?.enterprise_features?.payroll_module_flag;
	const hasEditTripsPermission = roleRestrictions.permission_trips_access.includes(user.role)
		? hasRequiredGateKeepers(user, { permission_trips_access: "editor" })
		: true;
	const hasRatesOnPermission = roleRestrictions.permission_rates_on.includes(user.role)
		? hasRequiredGateKeepers(user, { permission_rates_on: true })
		: true;
	const hasMessagingPermission = ["CARRIER_DRIVER", "CARRIER_MANAGER", "CARRIER_GENERIC"].includes(user.role)
		? hasRequiredGateKeepers(user, { permission_messaging_on: true })
		: true;
	const isProfitDisabled = !!trip?.carrierNotValid || !hasRatesOnPermission;

	const [openWarningDialog, setOpenWarningDialog] = useState(false);
	const [processing, setProcessing] = useState(false);
	const [tripUpdated, setTripUpdated] = useState(null);
	const [dialogTranscription, setDialogTranscription] = useState(null);
	const [rcFile, setRcFile] = useState(null);
	const [openDialogAlert, setDialogAlert] = useState({
		flag: false,
		message: null,
		type: null,
		optionsText: {},
	});
	const [paywallDialog, setPaywallDialog] = useState({});
	const [relayRoutes, setRelayRoutes] = useState([]);
	const [relayChildTrips, setRelayChildTrips] = useState([]);
	const [loadingRelayChildren, setLoadingRelayChildren] = useState(false);
	const [relayChildrenFailedCount, setRelayChildrenFailedCount] = useState(0);
	const [relayParentLoadId, setRelayParentLoadId] = useState(null);
	const [checkCallDialogOpen, setCheckCallDialogOpen] = useState(false);
	const [checkCallDefaultPhone, setCheckCallDefaultPhone] = useState("");
	const relayParentTripId = trip?.relay_parent_trip ?? trip?.data?.relay_parent_trip;

	const ignoreWarnings = useRef(false);

	const onClickNextState = useCallback(() => {
		const newStatus = getNextStatusExternal(trip);
		setProcessing(true);
		updateTripStatus({
			carrierId: trip.carrier,
			tripId: trip._id,
			status: newStatus.value,
			ignoreWarnings: ignoreWarnings.current,
		})
			.then(
				(data) => {
					dispatch(incrementDataRevision({ event: "tripsRevision" }));
					dispatch(incrementDataRevision({ event: "profileRevision" }));
					setProcessing(false);
					showSnackbar(snackbar, "Trip status updated", "success");
				},
				(err) => {
					setProcessing(false);
					const tripWarnings = err?.errors?.[0]?.metadata?.warnings;
					if (!tripWarnings?.length) {
						showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error updating the trip status", "error");
					} else {
						setOpenWarningDialog({
							warnings: tripWarnings,
							title: "Are you sure you want to continue?",
							message: err?.errors?.[0].message,
						});
					}
				}
			)
			.catch((err) => {
				setProcessing(false);
				console.log("ERROR", err);
				showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error updating the trip status", "error");
			});
	}, [dispatch, setProcessing, snackbar, trip, ignoreWarnings]);

	const onClickGenerateRelayTrips = useCallback(() => {
		setProcessing(true);
		generateOrUpdateRelayTrips(carrierId, tripId)
			.then(
				(data) => {
					dispatch(incrementDataRevision({ event: "tripsRevision" }));
					dispatch(incrementDataRevision({ event: "profileRevision" }));
					setProcessing(false);

					if (data.action === "created") {
						showSnackbar(snackbar, `Successfully generated ${data.relayTrips?.length || 0} relay trips`, "success");
					} else if (data.action === "updated") {
						showSnackbar(snackbar, `Successfully updated ${data.relayTrips?.length || 0} relay child trips`, "success");
					} else {
						showSnackbar(snackbar, data.message || "Relay trip operation completed", "success");
					}
				},
				(err) => {
					setProcessing(false);
					showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error processing relay trips", "error");
				}
			)
			.catch((err) => {
				setProcessing(false);
				console.log("ERROR", err);
				showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error processing relay trips", "error");
			});
	}, [dispatch, setProcessing, snackbar, carrierId, tripId]);

	const onClickRemoveRelayTrips = useCallback(() => {
		setProcessing(true);
		removeRelayTrips(carrierId, tripId)
			.then(
				(data) => {
					dispatch(incrementDataRevision({ event: "tripsRevision" }));
					dispatch(incrementDataRevision({ event: "profileRevision" }));
					setProcessing(false);
					showSnackbar(snackbar, `Successfully removed ${data.deletedTrips || 0} relay trips`, "success");
				},
				(err) => {
					setProcessing(false);
					showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error removing relay trips", "error");
				}
			)
			.catch((err) => {
				setProcessing(false);
				console.log("ERROR", err);
				showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error removing relay trips", "error");
			});
	}, [dispatch, setProcessing, snackbar, carrierId, tripId]);

	const handleOpenCheckCallDialog = useCallback(() => {
		setCheckCallDefaultPhone(extractDriverPhone(trip));
		setCheckCallDialogOpen(true);
	}, [trip]);

	const handleConfirmCheckCall = useCallback(
		(contactNumber) => {
			setProcessing(true);
			initiateCheckCall(carrierId, tripId, { contactNumber })
				.then(
					(data) => {
						setProcessing(false);
						setCheckCallDialogOpen(false);
						setCheckCallDefaultPhone(contactNumber);
						showSnackbar(snackbar, `Check call initiated for driver ${data.driverName || ""}`.trim(), "success");
					},
					(err) => {
						setProcessing(false);
						showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error initiating check call", "error");
					}
				)
				.catch((err) => {
					setProcessing(false);
					console.log("ERROR", err);
					showSnackbar(snackbar, err.errors?.[0]?.message ?? "Error initiating check call", "error");
				});
		},
		[carrierId, tripId, snackbar]
	);

	const onOpenTripView = useCallback(
		(targetTripId, targetCarrierId = carrierId) => {
			if (!targetTripId || !targetCarrierId) return;
			dispatch(
				openLoadedFormDialog({
					viewId: "TRIP_VIEW",
					dataIds: { tripId: targetTripId, carrierId: targetCarrierId, isExternal },
				})
			);
		},
		[carrierId, dispatch, isExternal]
	);

	useImperativeHandle(
		ref,
		() => {
			const btns = {};

			if (hasEditTripsPermission && !readOnly) {
				btns.edit = {
					onClick: () => {
						dispatch(
							openLoadedFormDialog({
								viewId: "TRIP_EDIT_VIEW",
								mode: "EDIT",
								dataIds: { tripId, carrierId, isExternal },
							})
						);
					},
					title: "Edit Trip",
				};

				if (hasRatesOnPermission) {
					btns.duplicate = {
						onClick: () => {
							dispatch(
								openLoadedFormDialog({
									viewId: "TRIP_EDIT_VIEW",
									mode: "CREATE",
									dataIds: {
										carrierId,
										isExternal: dataIds?.isExternal,
										rcDataEditable: {
											...trip,
											// Remove some fields
											_id: undefined,
											id: undefined,
											status: undefined,
											status__view: undefined,
											load_id: undefined,
											eta: undefined,
											pickup_date: undefined,
											delivery_date: undefined,
											pickup_window_date: undefined,
											delivery_window_date: undefined,
											accesorials: [],
											logistics_costs: [],
											pod_files: [],
											snapshots: undefined,
											rate_con_file: [],
											pod_file: [],
											rate_con_file__flag: false,
											pod_file__flag: false,
										},
										rcDataParsed: {},
										mode: "CREATE",
										tripId: null,
										DANGEROUS_onDone: (data) => {
											if (data) {
												// Close this caller
												dispatch(closeFormDialog());
											}
										},
									},
								})
							);
						},
						title: "Duplicate Trip",
						icon: "content_copy",
					};
				}
			}

			if (isInternal || isCarrier) {
				btns.share = {
					onSelect: (shareOption) => {
						onClickCreateUrl({ carrierId, tripId, form: "TRIP_VIEW", snackbar, option: hideRate(shareOption) });
					},
					options: RATE_OPTIONS,
					title: "Share",
				};
			}

			const status = trip?.status;
			if (isInternal || isCarrier) {
				if (status && status === "Delivered" && isEnabled("SEND_DRIVER_BOL_NOTIFICATION")) {
					btns.sendItinerary = {
						options: DRIVER_NOTIFICATION_OPTIONS,
						onSelect: async (selectedOption) => {
							if (selectedOption === "Send Driver Itinerary") {
								return sendItinerary(carrierId, tripId)
									.then(() => {
										dispatch(incrementDataRevision({ event: "profileRevision" }));
										showSnackbar(snackbar, "Itinerary Sent", "success");
									})
									.catch((e) => showSnackbar(snackbar, e?.errors?.[0]?.message ?? e.message, "error"));
							} else if (selectedOption === "Send Broker Update") {
								return sendBrokerUpdate(carrierId, tripId)
									.then(() => {
										dispatch(incrementDataRevision({ event: "profileRevision" }));
										showSnackbar(snackbar, "Broker update Sent", "success");
									})
									.catch((e) => showSnackbar(snackbar, e?.errors?.[0]?.message ?? e.message, "error"));
							} else if (selectedOption === "Send BOL Notification") {
								const onSubmitNotification = () => {
									return sendBolNotification(carrierId, tripId)
										.then(() => {
											dispatch(incrementDataRevision({ event: "profileRevision" }));
											showSnackbar(snackbar, "BOL Notification Sent", "success");
										})
										.catch((e) => showSnackbar(snackbar, e?.errors?.[0]?.message ?? e.message, "error"));
								};

								if (hasProfessionalRestrictions) {
									setPaywallDialog({
										open: true,
										dataIds: {
											type: "driver_notification_restriction",
											data: { dataIdsInherited: dataIds, callback: onSubmitNotification, callbackKey: "TRIP_VIEW" },
										},
									});
									return;
								} else {
									return onSubmitNotification();
								}
							}
							showSnackbar(snackbar, "Option not available", "error");
						},
						icon: "send",
					};
				} else if (status && status !== "Booked" && status !== "Not Booked" && status !== "Delivered") {
					btns.sendNotification = {
						onSelect: (selectedOption) => {
							if (selectedOption === "Send Driver Itinerary") {
								return sendItinerary(carrierId, tripId)
									.then(() => {
										dispatch(incrementDataRevision({ event: "profileRevision" }));
										showSnackbar(snackbar, "Itinerary Sent", "success");
									})
									.catch((e) => showSnackbar(snackbar, e?.errors?.[0]?.message ?? e.message, "error"));
							} else if (selectedOption === "Send Broker Update") {
								const onSubmitNotification = () => {
									return sendBrokerUpdate(carrierId, tripId)
										.then(() => {
											dispatch(incrementDataRevision({ event: "profileRevision" }));
											showSnackbar(snackbar, "Broker update Sent", "success");
										})
										.catch((e) => showSnackbar(snackbar, e?.message ?? e?.errors?.[0]?.message, "error"));
								};

								if (hasBasicRestrictions) {
									setPaywallDialog({
										open: true,
										dataIds: {
											type: "automate_functionality_restriction",
											data: { dataIdsInherited: dataIds, callback: onSubmitNotification, callbackKey: "TRIP_VIEW" },
										},
									});
									return;
								} else {
									return onSubmitNotification();
								}
							}
							showSnackbar(snackbar, "Option not available", "error");
						},
						options: TRIP_NOTIFICATION_OPTIONS,
						title: "Send notification",
					};
				}
			}

			if (isInternal || isCarrier) {
				// Add Relay Trip Management button for suitable trips
				// Only show for trips that are not already relay child trips and are in suitable status
				if (
					hasEditTripsPermission &&
					!trip?.is_relay_trip &&
					(trip?.status !== "Delivered" || trip?.status !== "Invoiced" || trip?.status !== "Paid")
				) {
					// For existing relay parent trips, show dropdown with update/remove options
					btns.relayTrips = {
						onSelect: (selectedOption) => {
							if (selectedOption === "Generate Relay Trips") {
								onClickGenerateRelayTrips();
							} else if (selectedOption === "Remove Relay Trips") {
								onClickRemoveRelayTrips();
							}
						},
						options: ["Generate Relay Trips", "Remove Relay Trips"],
						title: "Relay Trips",
						icon: "repeat",
					};
				}
			}

			if (hasMessagingPermission && trip?.truck && !dataIds?.openChat) {
				btns.chat = {
					onClick: () => {
						dispatch(
							openFormDialog({
								viewId: "CHAT_APP_PREVIEW_TRUCK_EXTERNAL",
								dataIds: { truckId: trip?.truck, carrierId: trip?.carrier },
							})
						);
					},
					icon: "chat_bubble_outline",
				};
			}

			const [fileId] = trip?.rate_con_file ?? [];
			if (isAdmin && fileId) {
				btns.save = {
					onClick: () => {
						dispatch(
							openFormDialog({
								viewId: "TRIP_TEST_DATA_VIEW",
								dataIds: { tripId, carrierId, fileId, urlFilePreview: buildLink(fileId) },
							})
						);
					},
					title: "Save for testing",
				};
			}

			dispatch(refreshDialog());
			return btns;
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[trip, dataIds, dispatch]
	);

	useEffect(() => {
		if (!trip) return;
		if (onProgress) onProgress(processing);
	}, [processing, onProgress, trip]);

	// Fetch relay routes when trip changes
	useEffect(() => {
		if (trip?.is_relay_parent && trip?.relay_trips?.length > 0) {
			setLoadingRelayChildren(true);
			getRelayTripData(trip, carrierId)
				.then(({ childTrips, routes, failedCount }) => {
					setRelayChildTrips(childTrips);
					setRelayRoutes(routes);
					setRelayChildrenFailedCount(failedCount);
				})
				.catch((error) => {
					console.error("Failed to load relay trip data:", error);
					setRelayChildTrips([]);
					setRelayRoutes([]);
					setRelayChildrenFailedCount(trip?.relay_trips?.length ?? 0);
				})
				.finally(() => {
					setLoadingRelayChildren(false);
				});
		} else {
			setRelayChildTrips([]);
			setRelayRoutes([]);
			setRelayChildrenFailedCount(0);
			setLoadingRelayChildren(false);
		}
	}, [trip, carrierId]);

	useEffect(() => {
		if (!trip?.is_relay_trip || !relayParentTripId) {
			setRelayParentLoadId(null);
			return;
		}

		const parentLoadFromChild = trip?.relay_parent_load_id ?? trip?.data?.relay_parent_load_id;
		if (parentLoadFromChild) {
			setRelayParentLoadId(parentLoadFromChild);
			return;
		}

		const parentCarrierId = trip?.carrier ?? carrierId;
		if (!parentCarrierId) {
			setRelayParentLoadId(null);
			return;
		}

		let unmounted = false;
		getTrip(relayParentTripId, parentCarrierId)
			.then((parentTrip) => {
				if (unmounted) return;
				setRelayParentLoadId(parentTrip?.data?.load_id ?? parentTrip?.load_id ?? null);
			})
			.catch((err) => {
				if (unmounted) return;
				console.warn("Failed to load relay parent trip for child disclaimer", err);
				setRelayParentLoadId(null);
			});

		return () => {
			unmounted = true;
		};
	}, [trip, relayParentTripId, carrierId]);

	const rcUploadEnable = useMemo(
		() => isInternal || isCarrier || isCarrierDriver,
		[isInternal, isCarrier, isCarrierDriver]
	);
	const openAddEventDialog = () => {
		let addDialog = { viewId: "TRIP_TRACKING", mode: "CREATE", dataIds: { carrierId, tripId, data: trip } };
		dispatch(openLoadedFormDialog(addDialog));
	};

	const handleDownload = (value) => {
		dispatch(
			openFormDialog({
				viewId: "FILE_VISUALIZER_VIEW",
				dataIds: {
					urlFilePreview: buildLink(value[0]),
				},
			})
		);
	};

	const onFileUpload = async (e) => {
		setProcessing(true);
		try {
			let response = await handleFileUpload(carrierId, trip, e.target.files);
			e.target.value = "";

			setRcFile(response?.rate_con_file);

			if (hasProfessionalRestrictions) {
				setTripUpdated(response);
				setProcessing(false);
				setDialogTranscription(_DEFAULT_TRANSCRIPTION);
				dispatch(
					openLoadedFormDialog({
						viewId: "TRIP_EDIT_VIEW",
						dataIds: { tripId, carrierId, isExternal, rcDataEditable: response },
					})
				);
				return;
			}

			if (response?.errors?.length > 0) {
				_FAILED_TRANSCRIPTION.message = `${_ERROR_PARSE_DOC} (${
					response?.errors?.[0]?.message ?? response?.errors?.message
				})`;

				setProcessing(false);
				setDialogTranscription(_FAILED_TRANSCRIPTION);
				return;
			}

			setTripUpdated(response);

			const locationCalc = await handledLocationsUpdated(response, trip);
			if (locationCalc) {
				response = { ...response, ...locationCalc };

				let message = `Recalculated empty miles (${locationCalc.emptymiles}), loaded miles (${locationCalc.miles}), and RPM (${locationCalc.rpm})`;
				showSnackbar(snackbar, message, "success");
			} else {
				const message = "Failed to racalculate miles...";
				showSnackbar(snackbar, message, "error");
			}

			setTripUpdated(response);
			setProcessing(false);
			setDialogTranscription(_SUCCESS_TRANSCRIPTION);
		} catch (error) {
			setProcessing(false);
			showSnackbar(snackbar, `There was an error uploading file`, "error", 5000);
		}
	};

	const onFileUploadGPT = async (e) => {
		const uploadId = uniqid();
		const response = await uploadFiles(Array.from(e.target.files), dispatch, uploadId, true, carrierId, true);
		const { fileId } = response;
		const rcDataEditable = { ...trip };
		rcDataEditable.rate_con_file = [fileId];
		rcDataEditable.rate_con_file__flag = true;
		dispatch(
			openLoadedFormDialog({
				viewId: "TRIP_EDIT_VIEW",
				dataIds: { tripId, carrierId, isExternal, rcDataEditable, uploadId },
			})
		);
	};

	const handleEditTranscription = () => {
		try {
			setDialogTranscription(_DEFAULT_TRANSCRIPTION);
			setProcessing(true);
			saveTripRC({ rcFile, tripId, carrierId }).then(
				(res) => {
					setProcessing(false);
					dispatch(
						openLoadedFormDialog({
							viewId: "TRIP_EDIT_VIEW",
							dataIds: { tripId, carrierId, isExternal, rcDataEditable: tripUpdated },
						})
					);
					setRcFile(null);
					setTripUpdated(null);
					dispatch(incrementDataRevision({ event: "tripsRevision" }));
					dispatch(incrementDataRevision({ event: "profileRevision" }));
				},
				(err) => {
					setRcFile(null);
					setProcessing(false);
					showSnackbar(snackbar, `There was an error saving rc file`, "error", 5000);
				}
			);
		} catch (err) {
			setRcFile(null);
			setProcessing(false);
			setDialogTranscription(_DEFAULT_TRANSCRIPTION);
			showSnackbar(snackbar, `There was an error saving rc file`, "error", 5000);
		}
	};

	const handleSaveTrip = () => {
		try {
			setDialogTranscription(_DEFAULT_TRANSCRIPTION);
			setProcessing(true);
			setRcFile(null);
			updateTrip(tripId, carrierId, tripUpdated).then(
				(res) => {
					setTripUpdated(null);
					setProcessing(false);
					dispatch(incrementDataRevision({ event: "tripsRevision" }));
					dispatch(incrementDataRevision({ event: "profileRevision" }));
				},
				(err) => {
					setTripUpdated(null);
					setProcessing(false);
					showSnackbar(
						snackbar,
						`There was an error saving trip transcription.(${err?.errors?.[0]?.message ?? err.message})`,
						"error",
						5000
					);
				}
			);
		} catch (err) {
			setRcFile(null);
			setProcessing(false);
			setDialogTranscription(_DEFAULT_TRANSCRIPTION);
			showSnackbar(
				snackbar,
				`There was an error saving trip transcription.(${err?.errors?.[0]?.message ?? err.message})`,
				"error",
				5000
			);
		}
	};

	const onCloseDialogTranscription = () => {
		setRcFile(null);
		setTripUpdated(null);
		setDialogTranscription(_DEFAULT_TRANSCRIPTION);
	};

	const openPaywallView = () => {
		dispatch(
			openFormDialog({
				viewId: "PAYWALL_VIEW",
				dataIds: {
					type: "automate_functionality_restriction",
					data: { dataIdsInherited: { ...dataIds, initTab: "TRACKING" }, callbackKey: "TRIP_VIEW" },
				},
			})
		);
	};

	if (!trip || error) {
		return <SmarthopDialogViewLoadingStub nativeMobile={nativeMobile} loading={!trip} error={error} />;
	}

	const relayType = trip?.relay_type ?? trip?.data?.relay_type;

	return (
		<SmarthopDialogViewContainer
			nativeMobile={nativeMobile}
			processing={processing}
			sideComponents={[
				{
					key: "TRIP_MAP_" + trip._id + (trip?.is_relay_parent ? "_RELAY_" + relayRoutes?.length : ""),
					type: "MAP",
					tabKeys: ["DETAILS", "TRACKING"],
					params: {
						locations: !trip?.is_relay_parent ? trip?.planLocations ?? trip?.all_locations : undefined,
						showFuelLocationsButton: true,
						loadId: !trip?.is_relay_parent ? trip?.load_id : undefined,
						carrierId,
						plan: !trip?.is_relay_parent ? tripPlan : undefined,
						routes: trip?.is_relay_parent && relayRoutes?.length > 0 && !loadingRelayChildren ? relayRoutes : [],
						truck: {
							label: trip?.truck__view?.label,
							lastLocation: trip?.truck__view?.metadata?.truck?.lastLocation,
							load_id: trip?.load_id,
						},
						truckLocation:
							STATUS_TRIPS.active.includes(trip.status) || STATUS_TRIPS.delivered.includes(trip.status)
								? trip?.truckLocation ?? trip?.last_location_coordinates
								: undefined,
					},
				},
			]}
			initTab={dataIds.initTab}
			tabComponents={[
				{
					key: "DETAILS",
					title: "Details",
					component: (
						<div className="flex flex-col w-full">
							{trip?.is_relay_trip && (
								<div className="bg-blue-50 border-1 border-blue rounded-lg p-10 mb-16">
									<div className="flex flex-row justify-between items-start">
										<div className="flex flex-row items-center">
											<Icon className="text-18 text-blue-800 mr-8">call_split</Icon>
											<div className="flex flex-col">
												<Typography className="text-blue-700 text-12 font-semibold">Relay Child Trip</Typography>
												<Typography className="text-blue-600 text-11">
													This trip is part of a relay chain{relayType ? ` (${relayType})` : ""}.
												</Typography>
												{relayParentLoadId && (
													<Typography className="text-blue-600 text-11">Parent Load #: {relayParentLoadId}</Typography>
												)}
												{!relayParentLoadId && relayParentTripId && (
													<Typography className="text-blue-600 text-11">Parent Trip ID: {relayParentTripId}</Typography>
												)}
											</div>
										</div>
										{relayParentTripId && !isPublicView && (
											<Button
												size="small"
												variant="outlined"
												className="ml-10 whitespace-nowrap"
												onClick={() => onOpenTripView(relayParentTripId, trip?.carrier ?? carrierId)}
											>
												Open Parent
											</Button>
										)}
									</div>
								</div>
							)}
							{trip?.is_relay_parent && relayRoutes?.length > 0 && (
								<div className="bg-blue-50 border-1 border-blue rounded-lg p-10 mb-16">
									<div className="flex flex-row items-center">
										<Icon className="text-18 text-blue-800 mr-8">route</Icon>
										<div className="flex flex-col">
											<Typography className="text-blue-700 text-12 font-semibold">Relay Parent Trip</Typography>
											<Typography className="text-blue-600 text-11">
												This trip includes {relayRoutes.length} relay routes displayed on the map
											</Typography>
										</div>
									</div>
								</div>
							)}
							{trip?.is_relay_parent && loadingRelayChildren && (
								<div className="bg-grey-50 border-1 border-grey rounded-lg p-10 mb-16">
									<div className="flex flex-row items-center">
										<Icon className="text-18 text-grey-600 mr-8 animate-spin">refresh</Icon>
										<Typography className="text-grey-600 text-12">Loading relay routes...</Typography>
									</div>
								</div>
							)}
							{trip?.is_relay_parent &&
								!loadingRelayChildren &&
								relayRoutes?.length === 0 &&
								trip?.relay_trips?.length > 0 && (
									<div className="bg-yellow-50 border-1 border-yellow rounded-lg p-10 mb-16">
										<div className="flex flex-row items-center">
											<Icon className="text-18 text-yellow-800 mr-8">warning</Icon>
											<div className="flex flex-col">
												<Typography className="text-yellow-700 text-12 font-semibold">Relay Parent Trip</Typography>
												<Typography className="text-yellow-600 text-11">
													Unable to load relay routes for display. Trip has {trip.relay_trips.length} associated relay
													trips.
												</Typography>
											</div>
										</div>
									</div>
								)}
							{trip && (
								<TripDescription
									isPublicView={isPublicView}
									priceRestricted={hidePrice}
									load={trip}
									invoice={!isPublicView && isTripInvoicingEligible && invoiceHasAccessView ? tripInvoice : null}
									processing={processing}
								/>
							)}
						</div>
					),
				},
				{
					key: "RELAY",
					title: "Relay",
					hideOnDesktop: isPublicView || !trip?.is_relay_parent,
					hideOnMobile: isPublicView || !trip?.is_relay_parent,
					component: (
						<div className="flex flex-col w-full">
							{loadingRelayChildren && (
								<div className="bg-grey-50 border-1 border-grey rounded-lg p-10 mb-16">
									<div className="flex flex-row items-center">
										<Icon className="text-18 text-grey-600 mr-8 animate-spin">refresh</Icon>
										<Typography className="text-grey-600 text-12">Loading relay child trips...</Typography>
									</div>
								</div>
							)}
							{!loadingRelayChildren && relayChildrenFailedCount > 0 && (
								<div className="bg-yellow-50 border-1 border-yellow rounded-lg p-10 mb-16">
									<div className="flex flex-row items-center">
										<Icon className="text-18 text-yellow-800 mr-8">warning</Icon>
										<Typography className="text-yellow-700 text-12">
											Unable to load {relayChildrenFailedCount} relay child{" "}
											{relayChildrenFailedCount === 1 ? "trip" : "trips"}.
										</Typography>
									</div>
								</div>
							)}
							{!loadingRelayChildren && relayChildTrips?.length > 0 && (
								<div className="w-full overflow-x-auto">
									<table className="min-w-full text-left border-collapse">
										<thead>
											<tr className="border-b-1 border-grey-300">
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Trip</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Relay Type</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Status</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Tracking</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Truck</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Driver</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Pickup</th>
												<th className="py-8 px-8 text-11 font-semibold text-grey-700">Delivery</th>
											</tr>
										</thead>
										<tbody>
											{relayChildTrips.map((childTrip) => {
												const childRelayType = childTrip?.relay_type ?? childTrip?.data?.relay_type ?? "-";
												const childStatus = childTrip?.status ?? childTrip?.data?.status ?? "-";
												const childDriver = childTrip?.driver__view?.label ?? "-";
												const childTrackingView = getRelayTrackingView(childTrip);
												return (
													<tr key={childTrip?._id} className="border-b-1 border-grey-200">
														<td className="py-8 px-8 text-12">
															<TripsCellView
																item={childTrip}
																view={getRelayTripView(childTrip)}
																variant="skinny"
																config={{
																	readOnly: false,
																	showBroker: true,
																	showStatus__top: true,
																}}
															/>
														</td>
														<td className="py-8 px-8 text-12">{childRelayType}</td>
														<td className="py-8 px-8 text-12">{childStatus}</td>
														<td className="py-8 px-8 text-12">
															{childTrackingView ? (
																<TrackingCellView
																	item={getRelayTrackingItem(childTrip)}
																	view={childTrackingView}
																	config={{ showETA: false, showLastUpdate: true, disableLink: true }}
																/>
															) : (
																"-"
															)}
														</td>
														<td className="py-8 px-8 text-12">
															<TruckCellView
																item={childTrip}
																view={getRelayTruckView(childTrip)}
																variant="skinny"
																config={{ readOnly: true }}
															/>
														</td>
														<td className="py-8 px-8 text-12">{childDriver}</td>
														<td className="py-8 px-8 text-12">{getRelayTripStopDisplay(childTrip, "PICKUP")}</td>
														<td className="py-8 px-8 text-12">{getRelayTripStopDisplay(childTrip, "DELIVERY")}</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
							)}
							{!loadingRelayChildren && relayChildTrips?.length === 0 && (
								<div className="bg-grey-50 border-1 border-grey rounded-lg p-10">
									<Typography className="text-grey-700 text-12">
										{trip?.relay_trips?.length > 0
											? "No relay child trips could be loaded."
											: "No relay child trips are associated with this parent trip."}
									</Typography>
								</div>
							)}
						</div>
					),
				},
				{
					key: "COMMENTS",
					title: "Comments",
					type: "LIST",
					hideOnDesktop: isPublicView,
					hideOnMobile: isPublicView,
					header: <TripCommentPanel tripId={dataIds.tripId} />,
					params: {
						config: tripCommentsList(nativeMobile),
						dataIds,
						renderEmptyListView: () => "Add your first comment to the trip",
					},
				},
				{
					key: "TRACKING",
					title: "Tracking",
					labelView: STATUS_TRIPS.active.includes(trip.status) && (
						<div
							className={
								"w-8 h-8 ml-4 rounded-full -mt-4" +
								(trip.tracking__view?.metadata?.outdated ? " bg-red " : " bg-green animate-pulse ")
							}
						/>
					),
					type: "LIST",
					hideOnDesktop: isPublicView,
					hideOnMobile: isPublicView,
					headerHeight: hasProfessionalRestrictions ? 105 : null,
					header: (
						<>
							{hasProfessionalRestrictions && (
								<div className="w-full pb-6">
									<Alert severity="info">
										Want to save time on manual trip updates?
										<Link className="cursor-pointer px-4" onClick={() => openPaywallView()}>
											<span className="text-primary font-semibold underline">Click here</span>
										</Link>
										to learn how we can automate trip status changes.
									</Alert>
								</div>
							)}
							<div
								className={`flex flex-row w-full items-center ${
									!STATUS_TRIPS.pending.includes(trip?.status) ? "justify-between" : "justify-end"
								} border-b-1 broger-grey mb-4`}
							>
								{!STATUS_TRIPS.pending.includes(trip?.status) && (
									<TrackingCellView
										item={trip}
										view={trip.tracking__view}
										config={{ showETA: false, showLastUpdate: true, disableLink: true }}
									/>
								)}
								<div className="flex flex-row space-x-4">
									{!isCarrierDriver && (
										<div className="flex flex-col pb-6">
											<Button
												variant="outlined"
												className={"bg-grey-100 text-12 border-1 whitespace-nowrap"}
												onClick={() => openAddEventDialog()}
											>
												Add Event
											</Button>
										</div>
									)}
									{!isCarrierDriver && isEnabled("ENABLE_PHONE_CALLS") && (
										<div className="flex flex-col pb-6">
											<Button
												variant="outlined"
												className={"bg-grey-100 text-12 border-1 whitespace-nowrap"}
												onClick={handleOpenCheckCallDialog}
												disabled={processing}
											>
												<Icon className="text-12 text-grey-600 mr-4">phone</Icon> Check Call
											</Button>
										</div>
									)}
									{isAdmin && (
										<div className="flex flex-col pb-6">
											<Button
												variant="outlined"
												className={"bg-grey-100 text-12 border-1"}
												onClick={() =>
													dispatch(
														openLoadedFormDialog({
															viewId: "DEBUG_TRACKING",
															dataIds: { carrierId, tripId, trip: trip },
														})
													)
												}
											>
												Debug Tools
											</Button>
										</div>
									)}
								</div>
							</div>
						</>
					),
					params: {
						config: loadHistoryList({ publicView: isPublicView }),
						dataIds: { carrierId, tripId },
						renderEmptyListView: () => "No Tracking Events Yet",
					},
				},
				{
					key: "PROFIT",
					title: "Profit",
					hideOnDesktop: isPublicView || (!isInternal && !isCarrier) || isProfitDisabled,
					hideOnMobile: isPublicView || (!isInternal && !isCarrier) || isProfitDisabled,
					component: (
						<div className="flex flex-col w-full">
							<CostsBreakdownPage data={trip} isView={true} />
						</div>
					),
				},
				{
					key: "PAYROLL",
					title: "Payroll",
					hideOnDesktop: isPublicView || !isPayrollEnabled || !trip?.payroll_elegible || !payrollHasAccessEdit,
					hideOnMobile: true,
					component: (
						<div className="flex flex-col w-full">
							<TripPayrollPage trip={trip} dataIds={dataIds} isView={true} />
						</div>
					),
				},
				{
					key: "INTERNAL",
					title: "Internal",
					hideOnDesktop: !isAdmin,
					hideOnMobile: !isAdmin,
					component: (
						<div className="flex flex-col w-full">
							<div className="w-full">
								{renderDataItem("Tracking ID", trip?.axleTracking?.id)}
								{renderDataItem("webhook_code", trip?.axleTracking?.webhook_code)}
								{renderDataItem("webhook_type", trip?.axleTracking?.webhook_type)}
							</div>
						</div>
					),
				},
			]}
			footerActions={[
				{
					key: "EDIT_TRIP",
					title: "Edit Trip",
					style: { align: "LEFT", icon: "edit" },
					hideOnDesktop: readOnly || !hasEditTripsPermission || !isLogged,
					hideOnMobile: readOnly || !hasEditTripsPermission || !isLogged,
					onClick: () => {
						dispatch(
							openLoadedFormDialog({
								viewId: "TRIP_EDIT_VIEW",
								mode: "EDIT",
								dataIds: { tripId, carrierId, isExternal },
							})
						);
					},
				},
				{
					key: "ADD_RC",
					title: "Upload RC",
					style: { align: "LEFT", icon: "publish" },
					hideOnDesktop: !rcUploadEnable || trip.rate_con_file__flag || !hasRatesOnPermission || !isLogged,
					hideOnMobile: !rcUploadEnable || trip.rate_con_file__flag || !hasRatesOnPermission || !isLogged,
					component: (
						<input
							onChange={!reactNative ? onFileUploadGPT : onFileUpload}
							id="ADD_RC"
							type="file"
							className="hidden"
						/>
					),
				},
				{
					key: "VIEW_RC",
					title: "View RC",
					style: { align: "LEFT", icon: "request_quote" },
					hideOnDesktop: !trip.rate_con_file__flag || !trip.rate_con_file || !hasRatesOnPermission || !isLogged,
					hideOnMobile: !trip.rate_con_file__flag || !trip.rate_con_file || !hasRatesOnPermission || !isLogged,
					onClick: () => {
						handleDownload(trip.rate_con_file, "rate_con_file");
					},
				},
				{
					key: "UPLOAD_FILES",
					title: "Upload Files",
					style: { align: "LEFT", icon: "note_add" },
					hideOnDesktop: !rcUploadEnable && isLogged,
					hideOnMobile: !rcUploadEnable && isLogged,
					onClick: () => {
						dispatch(
							openLoadedFormDialog({
								viewId: "UPLOAD_FILES_FORM",
								mode: "EDIT",
								dataIds: dataIds,
							})
						);
					},
				},
				{
					key: "STATUS_SELECT",
					style: { align: "RIGHT", notCollapsible: true, componentAlone: true },
					hideOnDesktop:
						((!hasEditTripsPermission || !STATUS_TRIPS.pending.includes(trip.status)) &&
							!STATUS_TRIPS.active.includes(trip.status)) ||
						!isLogged,
					hideOnMobile:
						((!hasEditTripsPermission || !STATUS_TRIPS.pending.includes(trip.status)) &&
							!STATUS_TRIPS.active.includes(trip.status)) ||
						!isLogged,
					component: <TripStatusSelector trip={trip} userId={dataIds?.userId} carrierId={carrierId} />,
				},
				{
					key: "MANAGE_INVOICES",
					title: "Manage Invoice",
					style: { align: "LEFT", icon: "receipt" },
					hideOnDesktop: !isTripInvoicingEligible || !invoiceHasAccessEdit || !isLogged,
					hideOnMobile: nativeMobile || !isTripInvoicingEligible || !invoiceHasAccessEdit || !isLogged,
					onClick: () => {
						if (invoiceHasAccessEdit || !hasBasicRestrictions) {
							dispatch(
								openLoadedFormDialog({
									viewId: "TRIP_INVOICE_VIEW",
									dataIds: dataIds,
								})
							);
							return;
						}
						setPaywallDialog({
							open: true,
							dataIds: {
								type: "default",
								data: { dataIdsInherited: dataIds, callbackKey: "TRIP_INVOICE_VIEW" },
							},
						});
					},
				},
				{
					key: "SEND_BOL",
					title: "Send Files",
					style: { align: "LEFT", icon: "send" },
					hideOnDesktop: !isManager || trip?.pod_files?.length < 1 || !isLogged,
					hideOnMobile: !isManager || trip?.pod_files?.length < 1 || !isLogged,
					onClick: () => {
						dispatch(
							openLoadedFormDialog({
								viewId: "FILE_SHARING_DIALOG",
								dataIds: { carrierId, tripId: trip._id, from: "TRIP" },
							})
						);
					},
				},
			]}
		>
			<TripCheckCallConfirmModal
				open={checkCallDialogOpen}
				trip={trip}
				initialPhone={checkCallDefaultPhone}
				processing={processing}
				onClose={() => setCheckCallDialogOpen(false)}
				onConfirm={handleConfirmCheckCall}
			/>
			<TripCreateTranscription
				data={tripUpdated}
				dialogConfig={dialogTranscription}
				onChange={(event, value) => {
					setTripUpdated({ ...tripUpdated, transcription_verified__flag: value });
				}}
				onAccept={() => {
					if (!tripUpdated) {
						handleEditTranscription();
					} else if (tripUpdated?.transcription_verified__flag) {
						handleSaveTrip();
					} else {
						setDialogAlert({
							flag: true,
							message: "Please check transcription verified checkbox to continue.",
							type: "transcription-alert",
							optionsText: { accept: "OK" },
						});
					}
				}}
				onEdit={() => handleEditTranscription()}
				onClose={onCloseDialogTranscription}
			/>
			<WarningConfirmDialog
				customMessage={openWarningDialog?.title}
				open={!!openWarningDialog}
				warnings={openWarningDialog?.warnings}
				onClose={() => {
					ignoreWarnings.current = false;
					setOpenWarningDialog(null);
				}}
				onAccept={() => {
					ignoreWarnings.current = true;
					setOpenWarningDialog(null);
					onClickNextState();
				}}
			/>
			<SmarthopConfirmDialog
				open={!!openDialogAlert.flag}
				title={openDialogAlert?.message}
				onAccept={() => setDialogAlert({ flag: false, message: null, type: null, optionsText: {} })}
				acceptMsg={openDialogAlert?.optionsText?.accept}
			/>

			<PaywallDialog data={paywallDialog} onHandlerPaywall={() => setPaywallDialog({})} />
		</SmarthopDialogViewContainer>
	);
});

export default TripView;
